import React, { FC, useCallback, useContext, useMemo, useState } from "react";

export interface TranslationCacheContextState {
  getCachedTranslation: (
    lang: Language,
    en: string | null | undefined,
  ) => string | null | undefined;
  addToTranslationCache: (args: {
    targetLang: Language;
    sourceText: string;
    targetText: string;
  }) => void;
  markTranslationsPending: (lang: Language, sourceTexts: string[]) => void;
  clearTranslationsPending: (lang: Language, sourceTexts: string[]) => void;
  isTranslationPending: (
    lang: Language,
    en: string | null | undefined,
  ) => boolean;
  hasPendingTranslations: (lang: Language) => boolean;
}

export const TranslationCacheContext = React.createContext<
  TranslationCacheContextState | undefined
>(undefined);

type Props = {
  children: React.ReactNode;
};

const TranslationCacheContextProvider: FC<Props> = ({ children }) => {
  const [translationCache, setTranslationCache] = useState<
    // the second map is from the english text to the translated text
    Map<Language, Map<string, string>>
  >(new Map());
  const getCachedTranslation = useCallback(
    (lang: Language, en: string | null | undefined) => {
      if (!en) {
        return en;
      }
      return translationCache.get(lang)?.get(en);
    },
    [translationCache],
  );
  const addToTranslationCache = useCallback(
    (args: {
      targetLang: Language;
      sourceText: string;
      targetText: string;
    }) => {
      setTranslationCache((prev) => {
        const { targetLang, sourceText, targetText } = args;
        if (sourceText === "") {
          return prev;
        }
        if (prev.get(targetLang)?.get(sourceText) === targetText) {
          return prev;
        }
        const newMap = new Map(prev);
        const innerMap = newMap.get(targetLang) || new Map();
        innerMap.set(sourceText, targetText);
        newMap.set(targetLang, innerMap);
        return newMap;
      });
    },
    [],
  );
  const [pendingTranslations, setPendingTranslations] = useState<
    Map<Language, Map<string, number>>
  >(new Map());
  const markTranslationsPending = useCallback(
    (lang: Language, sourceTexts: string[]) => {
      setPendingTranslations((prev) => {
        const next = new Map(prev);
        const langCounts = new Map(next.get(lang));
        sourceTexts.forEach((sourceText) => {
          langCounts.set(sourceText, (langCounts.get(sourceText) ?? 0) + 1);
        });
        next.set(lang, langCounts);
        return next;
      });
    },
    [],
  );
  const clearTranslationsPending = useCallback(
    (lang: Language, sourceTexts: string[]) => {
      setPendingTranslations((prev) => {
        const langCounts = prev.get(lang);
        if (!langCounts) return prev;
        const next = new Map(prev);
        const nextLangCounts = new Map(langCounts);
        sourceTexts.forEach((sourceText) => {
          const count = nextLangCounts.get(sourceText);
          if (!count) return;
          if (count === 1) {
            nextLangCounts.delete(sourceText);
          } else {
            nextLangCounts.set(sourceText, count - 1);
          }
        });
        next.set(lang, nextLangCounts);
        return next;
      });
    },
    [],
  );
  const isTranslationPending = useCallback(
    (lang: Language, en: string | null | undefined) =>
      !!en && !!pendingTranslations.get(lang)?.get(en),
    [pendingTranslations],
  );
  const hasPendingTranslations = useCallback(
    (lang: Language) => !!pendingTranslations.get(lang)?.size,
    [pendingTranslations],
  );
  const state: TranslationCacheContextState = useMemo(() => {
    return {
      getCachedTranslation,
      addToTranslationCache,
      markTranslationsPending,
      clearTranslationsPending,
      isTranslationPending,
      hasPendingTranslations,
    };
  }, [
    addToTranslationCache,
    clearTranslationsPending,
    getCachedTranslation,
    hasPendingTranslations,
    isTranslationPending,
    markTranslationsPending,
  ]);
  return (
    <TranslationCacheContext.Provider value={state}>
      {children}
    </TranslationCacheContext.Provider>
  );
};

export default TranslationCacheContextProvider;

export const useTranslationCacheContext = (): TranslationCacheContextState => {
  const contextVal = useContext(TranslationCacheContext);
  if (!contextVal) {
    throw Error("forgot to initialize TranslationCacheContext");
  }
  return contextVal;
};
